import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { store, useAppSelector } from '@/app/store';

import { checkNoError } from '@/features/channels/channelsSlice';
import {
  AUDIT_RETENTION_DEFAULT_DAYS,
  CheckoutDeviation,
  DELETED_RETENTION_DEFAULT_DAYS,
  MasterInfo,
  readCheckoutEnabled,
} from '@/features/master/masterSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';

import { RoleContext } from '@/app/api/common';
import { usePageSubscription, usePendingChanges } from '@/app/websocket/hooks';
import { GLOBAL_MASTER_ID } from '@/app/websocket/types';

import Providers, {
  ProviderEdit,
  buildProvidersPayload,
  providersInvalid,
  toProviderEdits,
} from '@/pages/Settings/Providers';

import Skyway, {
  SkywayEdit,
  buildSkywayPayload,
  skywayInvalid,
  toSkywayEdit,
} from '@/pages/Settings/Skyway';

import {
  InfoBanner,
  Pill,
  SettingsCard,
  SettingsInput,
  ToggleRow,
  su,
} from '@/pages/Settings/settingsUi';
function retentionInvalid(value: string): boolean {
  return !/^-?[0-9]+$/.test(value.trim());
}

// The window a raw value actually produces, so the three modes can be compared
// against each other. 0 is the built-in default and a negative is "never
// purge" — treating either as the number typed would compare the wrong thing.
function effectiveDays(days: number, fallback: number): number {
  if (!isFinite(days))	return NaN;
  if (days < 0)			return Infinity;	// never purge
  if (days === 0)		return fallback;

  return days;
}

const System = () => {
  const { t } = useTranslation();
  const role = React.useContext(RoleContext);
  const masterMain = useAppSelector((state) => state.master);

  // Local edit buffers. `disabled` mirrors the stored 2FA field verbatim; the
  // provider list is an edit copy carrying a per-row creds-dirty marker.
  const [disabled, setDisabled] = React.useState(!!masterMain.global_2fa_disabled);
  // Checkout switch (#91 rev 2, §3.1). Positive form like 2FA: true = on.
  const [checkout, setCheckout] = React.useState(readCheckoutEnabled(masterMain));
  // #91 §0.8 — read-only, straight from the store.
  const deviations: CheckoutDeviation[] = masterMain.checkout_deviations || [];
  const [providers, setProviders] = React.useState(toProviderEdits(masterMain.providers));
  const [skyway, setSkyway] = React.useState(toSkywayEdit(masterMain));
  const [retention, setRetention] = React.useState(String(masterMain.deleted_retention_days ?? 0));
  const [auditRetention, setAuditRetention] = React.useState(String(masterMain.audit_retention_days ?? 0));

  const [twoFAChanged, setTwoFAChanged] = React.useState(false);
  const [checkoutChanged, setCheckoutChanged] = React.useState(false);
  const [providersChanged, setProvidersChanged] = React.useState(false);
  const [skywayChanged, setSkywayChanged] = React.useState(false);
  const [retentionChanged, setRetentionChanged] = React.useState(false);
  const [auditRetentionChanged, setAuditRetentionChanged] = React.useState(false);
  const [triedSave, setTriedSave] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [remoteEdit, setRemoteEdit] = React.useState(false);

  React.useEffect(() => {
    store.getMaster();
  }, []);

  const resetFromStore = React.useCallback(() => {
    setDisabled(!!masterMain.global_2fa_disabled);
    setCheckout(readCheckoutEnabled(masterMain));
    setProviders(toProviderEdits(masterMain.providers));
    setSkyway(toSkywayEdit(masterMain));
    setRetention(String(masterMain.deleted_retention_days ?? 0));
    setAuditRetention(String(masterMain.audit_retention_days ?? 0));
    setRemoteEdit(false);
    setTwoFAChanged(false);
    setCheckoutChanged(false);
    setProvidersChanged(false);
    setSkywayChanged(false);
    setRetentionChanged(false);
    setAuditRetentionChanged(false);
    setTriedSave(false);
  }, [masterMain]);

  // Drop local edits whenever the Redux master refreshes (i.e. after a save).
  React.useEffect(() => {
    resetFromStore();
  }, [masterMain]);

  const changes = twoFAChanged || checkoutChanged || providersChanged || skywayChanged || retentionChanged ||
                  auditRetentionChanged;
  usePageSubscription([{ type: 'global' }]);
  const pendingChanges = usePendingChanges();

  const masterEventAt = React.useMemo(() => {
    return pendingChanges
      .filter((c) => c.type === 'global' && c.targetId === GLOBAL_MASTER_ID)
      .reduce((max, c) => (c.timestamp > max ? c.timestamp : max), 0);
  }, [pendingChanges]);

  const handledEventRef = React.useRef(0);

  React.useEffect(() => {
    if (!masterEventAt || masterEventAt <= handledEventRef.current)
      return;

    handledEventRef.current = masterEventAt;

    if (changes) {
      setRemoteEdit(true);
      return;
    }

    store.getMaster();
  }, [masterEventAt, changes]);

  const anyInvalid = React.useMemo(
    () => providersInvalid(providers) || skywayInvalid(skyway) ||
          retentionInvalid(retention) || retentionInvalid(auditRetention),
    [providers, skyway, retention, auditRetention]
  );

  const toggle2FA = (enabled: boolean) => {
    setDisabled(!enabled);
    setTwoFAChanged(true);
  };

  const toggleCheckout = (enabled: boolean) => {
    setCheckout(enabled);
    setCheckoutChanged(true);
  };

  const updateProviders = (next: ProviderEdit[]) => {
    setProviders(next);
    setProvidersChanged(true);
  };

  const updateSkyway = (next: SkywayEdit) => {
    setSkyway(next);
    setSkywayChanged(true);
  };

  const updateRetention = (next: string) => {
    setRetention(next);
    setRetentionChanged(true);
  };

  const updateAuditRetention = (next: string) => {
    setAuditRetention(next);
    setAuditRetentionChanged(true);
  };

  const handleSave = React.useCallback(async () => {
    if ((providersChanged && providersInvalid(providers)) ||
        (skywayChanged && skywayInvalid(skyway)) ||
        (retentionChanged && retentionInvalid(retention)) ||
        (auditRetentionChanged && retentionInvalid(auditRetention))) {
      setTriedSave(true);
      return;
    }

    const payload: Partial<MasterInfo> = {};
    if (twoFAChanged)
      payload.global_2fa_disabled = disabled;
    // Partial POST (§4.1): the key goes only when the switch was touched, so
    // saving another card never flips the Checkout.
    if (checkoutChanged)
      payload.checkout_enabled = checkout;
    if (providersChanged)
      payload.providers = buildProvidersPayload(providers);
    if (skywayChanged)
      Object.assign(payload, buildSkywayPayload(skyway));
    if (retentionChanged)
      payload.deleted_retention_days = parseInt(retention.trim(), 10);
    if (auditRetentionChanged)
      payload.audit_retention_days = parseInt(auditRetention.trim(), 10);

    setSaving(true);
    try {
      const mess = await store.setMaster(payload);
      if (!checkNoError(mess)) return;

      setTriedSave(false);
      setTwoFAChanged(false);
      setCheckoutChanged(false);
      setProvidersChanged(false);
      setSkywayChanged(false);
      setRetentionChanged(false);
      setAuditRetentionChanged(false);
      store.getMaster();
    } finally {
      setSaving(false);
    }
  }, [disabled, checkout, providers, skyway, retention, auditRetention,
      twoFAChanged, checkoutChanged, providersChanged, skywayChanged, retentionChanged,
      auditRetentionChanged]);

  const header = (
    <FlexRow direction="column" align="start" className="pb-[4px]">
      <span className={su.pageTitle}>{ t('system:caption') }</span>
    </FlexRow>
  );

  if (!role?.admin) {
    return (
      <div className={su.page}>
        <div className={su.pageInner}>
          { header }
          <SettingsCard icon="lock"
                        title={t('system:caption')}
                        subtitle={t('system:admin_only')} />
        </div>
      </div>
    );
  }

  // Present the POSITIVE form: checked = 2FA on.
  const twoFAEnabled = !disabled;
  const isLoaded = !masterMain.loading;

  // Admin implies the privilege, matching how the rest of the app reads ACLs.
  const canRefresh = !!role?.admin || !!role?.privilages?.includes('RefreshDeviceInfo');

  const storedRetention = masterMain.deleted_retention_days ?? 0;

  // Turns the raw number into the sentence that actually applies. 0 and the
  // negative case stay out of the "N days" wording — they are modes, not
  // durations, and reading them as a shorter window would be wrong.
  const retentionSummary = (days: number) => {
    if (	!isFinite(days) ||
			days === 0
	   )
      return t('system:deleted:summary:default', { n: DELETED_RETENTION_DEFAULT_DAYS });

    if (days < 0)
      return t('system:deleted:summary:never');

    return t('system:deleted:summary:days', { n: days });
  };

  const storedAuditRetention = masterMain.audit_retention_days ?? 0;

  const auditSummary = (days: number) => {
    if (	!isFinite(days) ||
			days === 0
	   )
      return t('system:audit:summary:default', { n: AUDIT_RETENTION_DEFAULT_DAYS });

    if (days < 0)
      return t('system:audit:summary:never');

    return t('system:audit:summary:days', { n: days });
  };

  // Escalate to the destructive-warning tone ONLY when the pending value is a
  // genuinely shorter window than the live one. Both sides are normalised
  // first, so 0 is compared as 180 and a negative as "forever" — extending to
  // never-purge must not trip the warning.
  // Deliberately not memoised: this sits below the non-admin early return, so a
  // hook here would be called conditionally and break hook order the moment
  // role.admin flips. It is two comparisons — there is nothing to memoise.
  const auditShortens = (() => {
    if (!auditRetentionChanged || retentionInvalid(auditRetention))
      return false;

    const next = effectiveDays(parseInt(auditRetention.trim(), 10), AUDIT_RETENTION_DEFAULT_DAYS);
    const live = effectiveDays(storedAuditRetention, AUDIT_RETENTION_DEFAULT_DAYS);

    return next < live;
  })();

  return (
  <React.Fragment>
    <div className={su.page}>
      <div className={su.pageInner}>
        { header }

        {/* Another admin saved /master.json while this form had edits pending.
            Discard is the safe resolution — it refetches. */}
        { remoteEdit &&
          <InfoBanner tone="warn" text={t('system:remote_edit')} />
        }

        {/* --- Security / Two-Factor ---
            The status pill reflects the STORED value, not the edit buffer, so
            admins can see what is actually live while staging a change. */}
        <SettingsCard icon="lock"
                      title={t('system:section:two_factor')}
                      subtitle={t('system:two_factor:subtitle')}
                      status={
                        <Pill tone={masterMain.global_2fa_disabled ? 'bad' : 'ok'}
                              dot={true}
                              label={masterMain.global_2fa_disabled
                                       ? t('system:two_factor:status_disabled')
                                       : t('system:two_factor:status_enabled')} />
                      }>
          <ToggleRow value={twoFAEnabled}
                     caption={t('system:two_factor:caption')}
                     description={t('system:two_factor:hint')}
                     setValue={toggle2FA} />


          { !twoFAEnabled &&
            <InfoBanner tone="warn" text={t('system:two_factor:warning')} />
          }
        </SettingsCard>

        {/* --- Checkout (#91 rev 2, §3.1) ---
            Built like the 2FA card: the pill shows the STORED value, the
            toggle the edit buffer. Off, MW light stays as it is and only the
            Checkout buttons are disabled; a waiting checkout stays stored (and
            cancellable) and goes out once it is switched back on. */}
        <SettingsCard icon="cleaning_services"
                      title={t('system:section:checkout')}
                      subtitle={t('system:checkout:subtitle')}
                      status={
                        // Off is the server default, not a fault: neutral, not red.
                        <Pill tone={readCheckoutEnabled(masterMain) ? 'ok' : 'info'}
                              dot={true}
                              label={readCheckoutEnabled(masterMain)
                                       ? t('system:checkout:status_on')
                                       : t('system:checkout:status_off')} />
                      }>
          <ToggleRow value={checkout}
                     caption={t('system:checkout:caption')}
                     description={t('system:checkout:hint')}
                     setValue={toggleCheckout} />
          {/* #91 §0.8 — the hotels whose own value differs from the STORED
              switch. Read-only; save() reads /master.json again, so the list
              follows the toggle once it is saved. Hidden when empty (and
              against an older server, which sends no list). */}
          {deviations.length > 0 &&
            <div className="flex flex-col gap-[4px] pt-[8px]">
              <div className="m14">
                { t('system:checkout:deviations') }
              </div>
            {deviations.map((d) =>
              <div key={d.id} className="flex flex-row items-center justify-between gap-[12px] r14">
                <Link to={`/projects/${encodeURIComponent(d.id)}`}
                      className="text-[--Primetime-main] hover:underline break-words">
                  { (d.name || d.id) + " (" + d.id + ")" }
                </Link>
                <Pill tone={d.checkout_enabled ? 'ok' : 'info'}
                      dot={true}
                      label={d.checkout_enabled
                               ? t('system:checkout:deviation_on')
                               : t('system:checkout:deviation_off')} />
              </div>
            )}
            </div>
          }
        </SettingsCard>

        {/* --- Live-TV Providers (KPN etc.) --- */}
        <SettingsCard icon="live_tv"
                      title={t('system:section:providers')}
                      titleSuffix={t('system:section:providers_suffix')}
                      subtitle={t('system:providers:hint')}>
          <Providers providers={providers}
                     setProviders={updateProviders}
                     triedSave={triedSave}
                     isLoaded={isLoaded} />
        </SettingsCard>

        {/* --- Skyway device inventory --- */}
        <SettingsCard icon="devices"
                      title={t('system:section:skyway')}
                      subtitle={t('system:skyway:subtitle')}
                      status={
                        <Pill tone={masterMain.device_info_api_secret_set ? 'ok' : 'warn'}
                              icon={masterMain.device_info_api_secret_set ? 'lock' : 'lock_open'}
                              label={masterMain.device_info_api_secret_set
                                       ? t('system:skyway:status_configured')
                                       : t('system:skyway:status_unconfigured')} />
                      }>
          <Skyway skyway={skyway}
                  setSkyway={updateSkyway}
                  secretSet={!!masterMain.device_info_api_secret_set}
                  triedSave={triedSave}
                  isLoaded={isLoaded}
                  canRefresh={canRefresh} />
        </SettingsCard>
        <SettingsCard icon="delete_outline"
                      title={t('system:section:deleted')}
                      subtitle={t('system:deleted:subtitle')}
                      status={
                        <Pill tone={storedRetention < 0 ? 'warn' : 'info'}
                              label={retentionSummary(storedRetention)} />
                      }>
          <SettingsInput label={t('system:deleted:days:caption')}
                         value={retention}
                         setValue={updateRetention}
                         type="text"
                         invalid={triedSave && retentionInvalid(retention)}
                         error={t('system:deleted:days:invalid')}
                         hint={retentionInvalid(retention)
                                 ? t('system:deleted:days:hint')
                                 : retentionSummary(parseInt(retention.trim(), 10))}
                         isLoaded={isLoaded}
                         className="max-w-[320px]" />

          {/* retain_until is stamped per tombstone at deletion time and never
              recomputed, so lowering this cannot retroactively destroy
              anything already in the bin. Say so — the opposite is the
              natural assumption. */}
          <InfoBanner text={t('system:deleted:no_retroactive')} />
        </SettingsCard>

        {/* --- Audit log retention ---
            Deliberately NOT a copy of the Recycle Bin card above: the sweep
            re-applies this window to every existing row on every pass, so
            lowering it destroys history that is already written. */}
        <SettingsCard icon="history"
                      title={t('system:section:audit')}
                      subtitle={t('system:audit:subtitle')}
                      status={
                        <Pill tone={storedAuditRetention < 0 ? 'warn' : 'info'}
                              label={auditSummary(storedAuditRetention)} />
                      }>
          <SettingsInput label={t('system:audit:days:caption')}
                         value={auditRetention}
                         setValue={updateAuditRetention}
                         type="text"
                         invalid={triedSave && retentionInvalid(auditRetention)}
                         error={t('system:audit:days:invalid')}
                         hint={retentionInvalid(auditRetention)
                                 ? t('system:audit:days:hint')
                                 : auditSummary(parseInt(auditRetention.trim(), 10))}
                         isLoaded={isLoaded}
                         className="max-w-[320px]" />

          { auditShortens
              ? <InfoBanner tone="warn" text={t('system:audit:retroactive_warning')} />
              : <InfoBanner text={t('system:audit:retroactive')} />
          }
        </SettingsCard>
      </div>
    </div>

    {/* Single action bar for the whole page. */}
    <FlexRow className="sticky bottom-0 z-20 bg-white border-t-1 border-solid border-[#E6E9EF] px-[32px] py-[14px]">
      <FlexRow flex={1} className="gap-[8px] items-center">
        <span className={[
                'material-icons !text-[18px]',
                changes ? '!text-[#B98B36]' : '!text-[#3BA05F]',
              ].join(' ')}>
          { changes ? 'edit' : 'check' }
        </span>
        <span className={[
                'text-[13px]',
                changes ? 'text-[#8A6A2F]' : 'text-[#5A6474]',
              ].join(' ')}>
          { changes ? t('system:save:unsaved') : t('system:save:saved') }
        </span>
      </FlexRow>
      <FlexRow flex={0} className="gap-[16px] items-center">
        <span className={[
                'text-[14px] font-medium select-none',
                changes && !saving
                  ? 'text-[#5A6474] cursor-pointer'
                  : 'text-[#B9C0CC] cursor-default',
              ].join(' ')}
              onClick={() => (changes && !saving) && resetFromStore()}>
          { t('system:save:discard') }
        </span>
        <Button caption={providersChanged
                           ? t('system:save:providers')
                           : t('system:save:caption')}
                icon="save"
                isDisabled={!changes || saving || (triedSave && anyInvalid)}
                onClick={() => handleSave()} />
      </FlexRow>
    </FlexRow>
  </React.Fragment>
  );
};

export default System;
